import React, { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

/**
 * Password input with show/hide toggle.
 * Matches Input component UI: rounded-xl, no shadow, same focus/error states.
 * @see src/components/ui/Input.js
 */
const PasswordInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  className = "",
  error,
  disabled = false,
  autoComplete = "current-password",
  size = "default",
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    default: "px-4 py-3 text-sm",
    lg: "px-4 py-3.5 text-base",
  };

  const baseClasses =
    "w-full bg-white border text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 ease-out focus:outline-none";

  const stateClasses = error
    ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
    : "border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20";

  const inputClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    "rounded-xl pr-11",
    disabled && "bg-gray-50 text-gray-500 cursor-not-allowed",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div>
      <div className="relative">
        <input
          id={id}
          name={name}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={inputClasses}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-20 flex items-center"
          tabIndex={-1}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? (
            <EyeSlashIcon className="w-5 h-5 shrink-0" />
          ) : (
            <EyeIcon className="w-5 h-5 shrink-0" />
          )}
        </button>
      </div>
      {typeof error === "string" && (
        <p className="mt-1.5 text-xs font-medium text-red-600 animate-fadeIn">
          {error}
        </p>
      )}
    </div>
  );
};

export default PasswordInput;
